import { useTranslations } from "next-intl";
import { useCallback, useMemo } from "react";

import MultiselectDropdown from "@zapier/reporting/src/components/global/MultiselectDropdown";
import RunStatusIcon from "@zapier/reporting/src/components/global/RunStatusIcon";
import {
  statusFilters,
  type StatusFilter as StatusFilterItem,
} from "@zapier/reporting/src/constants";
import { SizeType, useHasHydrated } from "@zapier/reporting/src/hooks";
import { StatusFilterType } from "@zapier/reporting/src/types";

type StatusFilterProps = {
  id?: string;
  menuAlign?: "right" | "left" | "stretch";
  onChange: (value: StatusFilterType[]) => void;
  placeholder?: string;
  size: SizeType;
  value: StatusFilterType[];
};

function StatusFilter({ onChange, placeholder, value }: StatusFilterProps) {
  const t = useTranslations("run-log.status-filter");
  const tStatus = useTranslations("run-status");
  const hasHydrated = useHasHydrated();

  // Labels come from the shared status namespace so a filter always reads the
  // same as the status it selects.
  const items = useMemo(
    () => statusFilters.map((f) => ({ ...f, label: tStatus(f.status[0]) })),
    [tStatus]
  );

  // Works around a hydration bug where the selected items can't be set until the component has hydrated
  const selectedItems = useMemo(
    () => (hasHydrated ? items.filter((f) => value.includes(f.value)) : []),
    [hasHydrated, items, value]
  );

  const getKeyForItem = useCallback((item: StatusFilterItem) => item.value, []);

  const handleChange = useCallback(
    (values: StatusFilterItem[]) =>
      onChange((values || []).map((v) => v.value)),
    [onChange]
  );

  const renderIcon = useCallback(
    (item: StatusFilterItem) => (
      <RunStatusIcon status={item.status[0]} size={24} />
    ),
    []
  );

  return (
    <MultiselectDropdown
      label={t("label")}
      getKeyForItem={getKeyForItem}
      items={items}
      onChange={handleChange}
      placeholder={placeholder || t("default-placeholder")}
      renderIcon={renderIcon}
      selectedItems={selectedItems}
      visibleItems={items.length}
    />
  );
}

export default StatusFilter;
